import {noop} from '@github-ui/noop'
import {createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState} from 'react'
import {useResizeObserver} from '@primer/react'
import {useActionBarRef} from './ActionBarRefContext'
import {calculateAvailableSpace, calculateItemOffsetWidth, calculateMenuSpace} from './utils'
import useIsomorphicLayoutEffect from '@primer/react/lib-esm/utils/useIsomorphicLayoutEffect'

type ActionBarResizeContextProps = {
  /**
   * The index of the key for the last `action` node that fits on the screen.
   */
  visibleChildEndIndex: number
  /**
   * Whether the justify-content CSS style for the outer container should be 'space-between' or not.
   */
  justifySpaceBetween: boolean
  /**
   * The overflow CSS style for the outer container of the action bar.
   */
  overflowStyle: string
  /**
   * Number (in Primer spacing units) for how much space should be between individual actions as well as between
   * actions and the overflow menu toggle button.
   */
  gap: number
  /**
   * Handler to trigger a recalculation of which action items should be visible versus hidden in the overflow menu.
   * Can be used in one of the given nested menus or nested select panels, such as if the selection of list items
   * changes in the select panel and that affects the width of the select panel toggle button rendered in the action
   * bar.
   */
  recalculateItemSize: (key: string, el: HTMLElement) => void
}

const ActionBarResizeContext = createContext<ActionBarResizeContextProps>({
  visibleChildEndIndex: 0,
  justifySpaceBetween: false,
  overflowStyle: 'hidden',
  gap: 0,
  recalculateItemSize: noop,
})

export type ActionBarResizeProviderValueProps = Pick<ActionBarResizeContextProps, 'gap'> & {
  actionKeys: string[]
}

type ActionBarResizeProviderProps = PropsWithChildren & {value: ActionBarResizeProviderValueProps}

export const ActionBarResizeProvider = ({children, value: {actionKeys, gap}}: ActionBarResizeProviderProps) => {
  const totalActions = actionKeys.length
  const [visibleChildEndIndex, setVisibleChildEndIndex] = useState(totalActions)
  const {outerContainerRef, itemContainerRef} = useActionBarRef()
  const [previousBarWidth, setPreviousBarWidth] = useState<number | undefined>()
  const [initialBarWidth, setInitialBarWidth] = useState<number | undefined>()
  const [itemOffsetWidths, setItemOffsetWidths] = useState<Map<string, number | undefined>>(
    new Map(actionKeys.map(key => [key, undefined])),
  )
  const [justifySpaceBetween, setJustifySpaceBetween] = useState<boolean>(false)
  const [overflowStyle, setOverflowStyle] = useState('hidden')
  const allItemWidthsCalculated = useMemo(
    () => actionKeys.every(key => itemOffsetWidths.has(key) && typeof itemOffsetWidths.get(key) === 'number'),
    [actionKeys, itemOffsetWidths],
  )
  const outerContainer = outerContainerRef.current
  const itemContainer = itemContainerRef.current

  const itemWidthAt = useCallback(
    (index: number) => {
      const key = actionKeys[index]
      if (key) return itemOffsetWidths.get(key)
    },
    [actionKeys, itemOffsetWidths],
  )

  const recalculateItemSize = useCallback(
    (key: string, el: HTMLElement) => {
      // If the item container isn't shown, none of the items are visible so we can't know their size
      if (!itemContainer) return

      const newWidth = calculateItemOffsetWidth(el, itemContainer)
      if (newWidth === itemOffsetWidths.get(key) || isNaN(newWidth)) return

      const newVal = new Map<string, number | undefined>(Array.from(itemOffsetWidths.entries()))
      newVal.set(key, newWidth)
      setItemOffsetWidths(newVal)
    },
    [itemOffsetWidths, itemContainer],
  )

  const shrink = useCallback(() => {
    // Don't try moving any items to the overflow menu if we haven't calculated yet how wide each item is when visible
    if (!allItemWidthsCalculated) return

    const availableSpace = calculateAvailableSpace(outerContainer, itemContainer)
    if (typeof availableSpace === 'undefined') return

    // Hide right-most item that's visible if there isn't enough space for it
    const menuSpace = calculateMenuSpace(outerContainer, itemContainer)
    if (typeof menuSpace === 'undefined') return

    if (availableSpace <= menuSpace) setVisibleChildEndIndex(Math.max(0, visibleChildEndIndex - 1))
  }, [allItemWidthsCalculated, visibleChildEndIndex, setVisibleChildEndIndex, itemContainer, outerContainer])

  const grow = useCallback(() => {
    const availableSpace = calculateAvailableSpace(outerContainer, itemContainer)
    if (typeof availableSpace === 'undefined') return

    // If we don't know how wide the next item is, we can't safely show it, so bail out
    const itemWidth = itemWidthAt(visibleChildEndIndex)
    if (typeof itemWidth === 'undefined') return

    const menuSpace = calculateMenuSpace(outerContainer, itemContainer)
    if (typeof menuSpace === 'undefined') return

    if (availableSpace > menuSpace + itemWidth) {
      setVisibleChildEndIndex(Math.min(totalActions, visibleChildEndIndex + 1))
    }
  }, [itemWidthAt, totalActions, visibleChildEndIndex, setVisibleChildEndIndex, outerContainer, itemContainer])

  const update = useCallback(() => {
    if (!outerContainer) return

    const currentBarWidth = outerContainer.offsetWidth
    if (typeof previousBarWidth === 'undefined' || currentBarWidth <= previousBarWidth) shrink()
    else if (currentBarWidth > previousBarWidth) grow()

    setPreviousBarWidth(currentBarWidth)
    setJustifySpaceBetween(typeof initialBarWidth === 'number' && currentBarWidth <= initialBarWidth)
  }, [grow, shrink, initialBarWidth, previousBarWidth, outerContainer])

  useIsomorphicLayoutEffect(() => {
    setOverflowStyle('hidden')
    setVisibleChildEndIndex(totalActions)
  }, [totalActions])

  useIsomorphicLayoutEffect(() => {
    if (itemContainer) setInitialBarWidth(itemContainer.offsetWidth)
  }, [itemContainer])

  useIsomorphicLayoutEffect(() => {
    setOverflowStyle('hidden')
    const timeout = setTimeout(() => {
      setOverflowStyle('visible')
      update()
    }, 20) // Wait for the items to be rendered, making this really short to avoid a flash of unstyled content
    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [update])

  useResizeObserver(update, outerContainerRef)

  const value = useMemo<ActionBarResizeContextProps>(
    () => ({visibleChildEndIndex, justifySpaceBetween, overflowStyle, gap, recalculateItemSize}),
    [visibleChildEndIndex, justifySpaceBetween, overflowStyle, gap, recalculateItemSize],
  )
  return <ActionBarResizeContext.Provider value={value}>{children}</ActionBarResizeContext.Provider>
}

export const useActionBarResize = () => {
  const context = useContext(ActionBarResizeContext)
  if (!context) throw new Error('useActionBarResize must be used with ActionBarResizeProvider.')
  return context
}

try{ ActionBarResizeContext.displayName ||= 'ActionBarResizeContext' } catch {}
try{ ActionBarResizeProvider.displayName ||= 'ActionBarResizeProvider' } catch {}